import React from 'react';

import { asyncTimeout } from 'utils/asyncTimeout';

export type AsyncState<TData> = {
  pending: boolean;
  resolved: boolean;
  resolvedWith: null | TData;
  rejected: boolean;
  rejectedWith: null | Error;
};

export type AsyncStateHookOptions = {
  throwError?: boolean;
  minimalPendingDelay?: number;
};

export type AsyncFunction<TParams, TData> = TParams extends void
  ? () => Promise<TData>
  : (params: TParams) => Promise<TData>;

const getInitialState = <TData>(): AsyncState<TData> => ({
  pending: true,
  resolved: false,
  resolvedWith: null,
  rejected: false,
  rejectedWith: null,
});

export const useAsyncState = <TParams = void, TData = void>(
  asyncFunction: AsyncFunction<TParams, TData>,
  options: AsyncStateHookOptions = {}
) => {
  const { throwError = false, minimalPendingDelay = 0 } = options;

  const actualPromiseRef = React.useRef<null | Promise<TData>>(null);
  const unmountedRef = React.useRef<boolean>(false);

  const [asyncState, setAsyncState] = React.useState<null | AsyncState<TData>>(
    null
  );

  if (asyncState && asyncState.rejectedWith) {
    if (throwError === true) {
      throw asyncState.rejectedWith;
    } else {
      console.error(asyncState.rejectedWith.message);
    }
  }

  const call = React.useCallback(
    (params: TParams) => {
      const promise = asyncFunction(params);

      const delayPromise = asyncTimeout(minimalPendingDelay);

      actualPromiseRef.current = promise;

      const handleResolve = async (nextData: TData) => {
        await delayPromise;

        if (!unmountedRef.current && promise === actualPromiseRef.current) {
          setAsyncState({
            pending: false,
            resolved: true,
            resolvedWith: nextData,
            rejected: false,
            rejectedWith: null,
          });
        }
      };

      const handleReject = async (nextError: Error) => {
        await delayPromise;

        if (!unmountedRef.current && promise === actualPromiseRef.current) {
          setAsyncState({
            pending: false,
            resolved: false,
            resolvedWith: null,
            rejected: true,
            rejectedWith: nextError,
          });
        }
      };

      setAsyncState(getInitialState());

      promise.then(handleResolve, handleReject);
    },
    [asyncFunction, minimalPendingDelay]
  );

  return [asyncState, call] as const;
};
